/**
 * AiChatBot engine configuration — single source of truth for tunable values.
 *
 * Every constant exposed by the chat engine lives here. Tests assert against the
 * values by import, not by hard-coding the number. Tuning a threshold is always
 * a one-file change.
 *
 * Shape groups (kept flat-ish so `AI_CHATBOT_CONFIG.foo.bar` is the only access
 * pattern — no deep-dot chains):
 *
 * - models:           per-stage model IDs
 * - contextWindow:    token budgets + compaction triggers
 * - toolCache:        TTLs for read-tool memoisation
 * - conversationStore: per-entity sessionStorage cache
 * - runawayLoopGuard: client-side UX safeguard for write ops — NOT security
 * - summarization:    when CrmBot summary functions bail out of full-detail mode
 * - queryDefaults:    default filter params for list/user-scoped read tools
 * - ui:               visible timings and render limits
 * - debug:            default debug level for instances
 */

export enum JrModel {
  Standard = 'jr-auto/standard', // claude-sonnet-4-6
  Fast = 'jr-auto/fast', // claude-haiku-4-5
  Powerful = 'jr-auto/powerful', // claude-opus-4-6
}

export type AiChatBotDebugLevel = 'off' | 'console' | 'verbose'

export const AI_CHATBOT_CONFIG = {
  /**
   * Per-stage model IDs for the pipeline. Stage order is fixed
   * (guard → gather → synthesize). Only the model IDs are tunable.
   * Swapping here is the entire "change which model" surface — no other file
   * should hard-code a model string.
   */
  models: {
    guard: JrModel.Fast,
    gather: JrModel.Fast,
    synthesize: JrModel.Standard,
    // Compaction summarises long histories — haiku produces hallucinated summaries and wrong counts.
    compact: JrModel.Standard,
    // Write-intent classifier — fast model sufficient for 3-way read/write/ambiguous decision.
    classifier: JrModel.Fast,
  },

  /**
   * Context window + compaction thresholds.
   *
   * - totalTokens:                  app-level token budget. Sonnet 4.6 supports 200K; we compact at 60K to keep
   *                                  costs predictable. This is not the model's hard limit.
   * - autoCompactBufferTokens:       when remaining < this, compact before next turn
   * - postCompactBudgetTokens:       summary + recent turns must fit under this
   * - maxConsecutiveCompactFailures: circuit breaker — disables new turns after N
   *                                  consecutive compaction failures; user sees
   *                                  "Conversation too long — please start a new one"
   * - preserveRecentTurns:           always keep this many most-recent turns out of the
   *                                  summarised region so near-term context survives
   *
   * Token accounting uses the same tokenizer as the synthesize-stage model to stay
   * honest with what the API will actually count.
   */
  contextWindow: {
    totalTokens: 60_000,
    autoCompactBufferTokens: 15_000,
    postCompactBudgetTokens: 25_000,
    maxConsecutiveCompactFailures: 3,
    preserveRecentTurns: 6,
    compactionDisabledMessage:
      'Conversation too long — please start a new one.',
  },

  /**
   * TTLs for read-tool result caching. Write tools are never cached.
   * Single-entity detail cache lives longer than list queries because the
   * underlying record changes less often than a list's membership.
   */
  toolCache: {
    singleTtlMs: 15 * 60 * 1000,
    listTtlMs: 5 * 60 * 1000,
  },

  /**
   * Per-entity conversation cache + history list.
   *
   * - ttlMs:               how long a conversation survives without activity
   * - maxEntries:          FIFO cap on entries in the store (per instance)
   * - undoDeleteWindowMs:  how long the "undo delete" ribbon stays in the history drawer
   */
  conversationStore: {
    maxEntries: 20,
    undoDeleteWindowMs: 5_000,
  },

  /**
   * Confirmed-entity slot system. Per-conversation typed slots
   * populated when the user resolves a name via show_options (single-select).
   * The deictic disambiguation rule in the system prompt reads from these slots
   * for pronouns and bare types; explicitly named entities always re-search.
   *
   * - maxSlots: most-recent-first FIFO cap. Higher values bloat the prompt
   *             with stale references; lower values lose useful context.
   *             5 covers typical "compare A, B; switch to C" workflows.
   */
  confirmedEntities: {
    maxSlots: 5,
  },

  /**
   * Runaway-loop guard. This is a CLIENT-SIDE UX SAFEGUARD, not a
   * security control. PISA permissions are authoritative. The guard exists to
   * stop the bot from accidentally creating 50 tickets in a tight loop while
   * the user is mid-confirmation — it caps writes *per local tab session*.
   *
   * - creates/updates per window; sliding window of windowMs
   */
  runawayLoopGuard: {
    createsPerWindow: 5,
    updatesPerWindow: 10,
    windowMs: 60_000,
  },

  /**
   * Per-turn safety guards for the tool-call loop (`runLoop` iterates once per
   * model completion).
   *
   * - maxErrors: PRIMARY guard. A model stuck retrying a failing tool (e.g. an
   *   invalid enum value) errors every round; a healthy flow — even a data-heavy
   *   summary firing many tools — errors ~never, so this never trips on
   *   legitimate work. Small margin tolerates a transient error or two before
   *   the model recovers.
   * - maxRounds: BACKSTOP. A cheap absolute ceiling against a non-error infinite
   *   loop (a model that keeps emitting *successful* tool calls without ever
   *   answering). Set high enough that no legitimate flow reaches it.
   *
   * When either trips, the turn ends with a user-visible message instead of
   * hanging in the thinking state.
   */
  toolLoop: {
    maxErrors: 5,
    maxRounds: 25,
    limitReachedMessage:
      "I couldn't complete that after several attempts. Please rephrase your request or try again.",
  },

  /**
   * CrmBot summary functions switch to summary-mode when the dataset crosses
   * these thresholds. Kept here because several functions read them.
   * See `.claude/summarization-strategy.md` for the rationale on each number.
   */
  summarization: {
    maxActivitiesForDetail: 100,
    maxMessagesForDetail: 100,
  },

  /**
   * Default filter params for list-shaped read tools.
   *
   * Every user-scoped and list-read tool declares its filter params `.optional()`
   * and fills in these defaults in `run()` before hitting PISA.
   *
   * - listLimit:           default page size when the user did not specify
   * - listLimitCap:        hard cap — bot never requests more than this without
   *                        an explicit user ask
   * - daysBack:            default lookback window for "recent" / unspecified recency
   * - narrowingThreshold:  when a tool returns > this many rows, synthesis renders
   *                        an aggregate summary + narrowing follow-up chips
   *                        instead of per-row detail
   */
  queryDefaults: {
    listLimit: 20,
    listLimitCap: 100,
    daysBack: 30,
    narrowingThreshold: 50,
  },

  /**
   * Visible UI timings and render limits.
   *
   * - messageListPreviewLineClamp: conversation-history drawer uses CSS
   *   `-webkit-line-clamp` to truncate the first-prompt preview responsively.
   *   The full prompt is stored; only rendering clamps.
   */
  ui: {
    messageListPreviewLineClamp: 2,
  },

  /**
   * Guard stage configuration.
   *
   * - blockedMessage: shown to the user when the injection guard rejects a message.
   *   Engine code cannot use i18n hooks, so this lives here as a tuneable string.
   */
  guard: {
    blockedMessage: 'I can only help with CRM questions.',
  },

  /**
   * Default debug level for newly-constructed Conversation / AiChatBot instances.
   * Individual instances may override via the `debug` prop. See `engine/logger.ts`
   * for the per-level emission contract.
   *
   * buildConfig() in functions/index.ts always overrides this via resolveDebugLevel():
   * dev = 'verbose', production = 'off' unless ?aiDebug= URL param is set.
   * This value is only the fallback for direct Conversation construction outside buildConfig.
   */
  debug: {
    defaultLevel: 'verbose',
  },
} as const

/**
 * Deep-readable type alias so consumers can spell out exactly what they
 * depend on without re-importing the whole config object.
 */
export type AiChatBotConfig = typeof AI_CHATBOT_CONFIG
